import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ContactFAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      question: "How secure is WhatsApp communication with Nitin?",
      answer: `All WhatsApp conversations with Nitin are end-to-end encrypted and completely secure. We never share your personal information or gaming preferences with third parties.\n\nOur team follows strict confidentiality protocols, and all conversations are protected by WhatsApp's advanced security features. Your privacy and security are our top priorities.`
    },
    {
      question: "What types of support can I get through WhatsApp?",
      answer: `Our WhatsApp support covers everything you need:\n\n• Game recommendations and strategies\n• Account setup and verification\n• Payment and withdrawal assistance\n• Bonus and promotion guidance\n• Technical troubleshooting\n• VIP services and exclusive offers\n• Responsible gaming support\n\nOur experts are trained to handle all aspects of your gaming experience.`
    },
    {
      question: "How quickly will I get a response on WhatsApp?",
      answer: `Our average response time is under 2 minutes during business hours (9 AM - 11 PM IST). For urgent matters, our VIP support team responds within 30 seconds.\n\nEven during off-hours, you'll receive an automated acknowledgment immediately, and our team will respond first thing in the morning.`
    },
    {
      question: "Can I schedule video calls with gaming experts?",
      answer: `Absolutely! We offer personalized video consultations for:\n\n• Strategy discussions for high-stakes games\n• Portfolio review and optimization\n• New player onboarding sessions\n• VIP account management meetings\n\nSimply message us on WhatsApp to schedule a convenient time for your video consultation.`
    },
    {
      question: "Do you provide support in regional languages?",
      answer: `Yes! Our multilingual team provides support in:\n\n• Hindi\n• English\n• Punjabi\n• Gujarati\n• Marathi\n• Telugu\n• Tamil\n• Bengali\n\nJust let us know your preferred language when you first contact us, and we'll connect you with the right expert.`
    },
    {
      question: "Is there a cost for WhatsApp support services?",
      answer: `All basic support services through WhatsApp are completely free for Nitin players. This includes:\n\n• General gaming advice\n• Account assistance\n• Payment support\n• Basic strategy guidance\n\nPremium services like detailed game analysis and personalized coaching sessions may have associated fees, which will be clearly communicated upfront.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="HelpCircle" size={32} color="white" strokeWidth={2} />
            </div>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-gradient-royal mb-6">
            WhatsApp Support FAQ
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Get answers to common questions about our WhatsApp support services and how we can help enhance your gaming experience.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs?.map((faq, index) => (
            <div 
              key={index}
              className="bg-surface rounded-card shadow-subtle overflow-hidden border border-border hover:border-golden transition-royal"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-muted transition-royal"
              >
                <h3 className="text-lg font-heading font-semibold text-text-primary pr-4">
                  {faq?.question}
                </h3>
                
                <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center transition-royal ${
                  openFAQ === index ? 'rotate-180 bg-primary text-white' : ''
                }`}>
                  <Icon 
                    name="ChevronDown" 
                    size={20} 
                    color={openFAQ === index ? 'white' : 'var(--color-primary)'} 
                    strokeWidth={2}
                  />
                </div>
              </button>
              
              {openFAQ === index && (
                <div className="px-6 pb-6">
                  <div className="pt-4 border-t border-border">
                    <div className="prose prose-sm max-w-none">
                      {faq?.answer?.split('\n')?.map((paragraph, pIndex) => (
                        <p key={pIndex} className="text-text-secondary leading-relaxed mb-3 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 rounded-card p-8 border border-golden">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                <Icon name="MessageCircle" size={24} color="white" strokeWidth={2} />
              </div>
            </div>
            
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-4">
              Still Have Questions?
            </h3>
            
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              Our gaming experts are ready to answer any specific questions you might have. Get personalized assistance right now.
            </p>
            
            <button
              onClick={() => window.open('https://wa.me/917704070169?text=Hi! I have some questions about your WhatsApp support services. Can you help me?', '_blank')}
              className="bg-gradient-royal text-white px-8 py-4 rounded-button font-semibold hover:shadow-glow transition-royal animate-pulse-golden hover:animate-none inline-flex items-center space-x-3"
            >
              <Icon name="MessageCircle" size={20} color="white" strokeWidth={2} />
              <span>Ask Our Experts</span>
            </button>
            
            <div className="flex items-center justify-center space-x-6 mt-6 text-sm text-text-muted">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>3 experts online</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} color="var(--color-primary)" />
                <span>Response in &lt; 2 minutes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFAQ;